import { InputProps } from 'antd';
import { union } from 'lodash';
import { pipe } from 'ramda';
import React, { forwardRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Input } from 'components/atoms/input';
import { OutlineError } from 'components/styles';
import { Just, map, Nothing, orElse, valueOf } from 'core/maybe';
import { formatterWithMinutes } from './formatter';
import {
  durationParser,
  Parser,
  withDefaultValue,
  withMaximum,
  withMinimum,
} from './parser';
import { useInputDuration, getError, getMinutes } from './use-input-duration';

const DEFAULT_VALUE = -1;

export const StyledInput = styled(Input)`
  &.has-error {
    ${OutlineError};
    &:focus {
      box-shadow: none;
      ${OutlineError};
    }
  }
`;

export interface InputDurationProps extends Omit<InputProps, 'onChange'> {
  onChange?: (minutes: number) => void;
  testId?: string;
}
export const InputDuration = forwardRef<any, InputDurationProps>(
  (
    {
      value,
      min,
      max,
      className,
      onPressEnter,
      onBlur,
      onChange: onMinutesChange,
      testId = 'input-duration',
      ...restProps
    },
    ref,
  ) => {
    const transformers = union<any>(
      min != null ? [withMinimum(parseInt(min.toString(), 10))] : [],
      max != null ? [withMaximum(parseInt(max.toString(), 10))] : [],
      [withDefaultValue(0)],
    );
    const parser = transformers.reduce(
      (p, t) => t(p),
      durationParser,
    ) as Parser<
      number,
      'invalid_pattern' | 'zero' | 'less_than_minimum' | 'exceeds_maximum'
    >;

    const {
      value: inputValue,
      handleChange: inputHandleChange,
      handlePressEnter,
      handleBlur,
      state,
    } = useInputDuration(value ? Just(value as number) : Nothing(), parser);

    const duration = useMemo(() => getMinutes(state), [state]);
    const error = useMemo(() => getError(state), [state]);

    useEffect(() => {
      const finalValue = pipe(
        () => duration,
        map((x) => x),
        orElse(DEFAULT_VALUE),
      )();

      if (finalValue === DEFAULT_VALUE) {
        return;
      }

      onMinutesChange?.(finalValue);
    }, [duration, onMinutesChange]);

    return (
      <StyledInput
        data-cy={testId}
        ref={ref}
        className={`${className} ${valueOf(error) != null ? 'has-error' : ''}`}
        min={min}
        max={max}
        value={inputValue ?? formatterWithMinutes(DEFAULT_VALUE)}
        onChange={inputHandleChange}
        onPressEnter={(e) => {
          handlePressEnter(e);
          onPressEnter?.(e);
        }}
        onBlur={(e) => {
          handleBlur(e);
          onBlur?.(e);
        }}
        {...restProps}
      />
    );
  },
);
